import Vue from "vue";
import Vuex from "vuex";
import axios from "axios";
import hardcodedBranding from "../hardcodedBranding";
import createPersistedState from "vuex-persistedstate";
import config from "../config/constants";
import moment from "moment";
import EventBus from "../helpers/eventbus";
import {
  identifyUser,
  resetUser,
  registerLang,
  trackEvent,
} from "../utils/analytics/analytics";
import {
  fetchUserTimezone,
  isTimezoneSupportEnabled,
} from "../utils/timezone/timezone";
import { setDisplayTimeZone } from "../utils/dateTime/dateTime";
import { APP_OPENED } from "../utils/analyticsEvents";
import {
  createInFlightGuard,
  EMPTY_TWO_FACTOR_CHALLENGE,
  persistTwoFactorChallenge,
  readStoredTwoFactorChallenge,
  resendTwoFactorCode,
  sendTwoFactorCode,
  TWO_FACTOR_SEND_INVALID_PHONE_MESSAGE,
} from "../utils/twoFactor/twoFactor";
import {
  identifyIntercomUser,
  resetIntercomUser,
} from "../utils/intercom/intercom";
import { signOutWebchat } from "../utils/webchat/webchat";
import dynamicThemePlugin from "../theming/dynamicThemePlugin";
Vue.use(Vuex);

// dateTime's converter is a module-level value, so it has to be re-applied from
// the store — including the zone vuex-persistedstate rehydrates before any fetch
// finishes. Registered after createPersistedState so the state is already hydrated.
const displayTimeZonePlugin = (store) => {
  // Australia-only (TD-192) — including a zone vuex-persistedstate rehydrated from
  // before the feature was gated. Leaving it unset renders UTC instants device-local.
  if (!isTimezoneSupportEnabled()) return;
  setDisplayTimeZone(store.state.userTimezone);
  store.subscribe((mutation, state) => {
    if (mutation.type === "setUserTimezone" || mutation.type === "logout") {
      setDisplayTimeZone(state.userTimezone);
    }
  });
};

const twoFactorSignInGuard = createInFlightGuard();
const twoFactorResendGuard = createInFlightGuard();

const defaultUser = {
  authenticated: false,
  success: false,
  up_domain: "up",
  insights_completed: false,
  user_client_id: Number,
  user_id: String,
  user_role: "cl",
  user_token: String,
  username: String,
  user_pp_slug: "generic_pp",
  user_tc_slug: "generic_tc",
  v3_ready: true,
  home_page: "old_default",
  access_code_user: false,
  therapy: {
    area: null,
    age_group: null,
    membership_id: null,
  },
  user: {
    first_name: String,
    last_name: String,
    email: String,
    email_verified: null,
    employer_admin: Boolean,
    is_nep_enabled: Boolean,
    course_progress: [],
    my_list: [],
    country: "Ireland",
  },
  user_profile: {
    image_url: String,
    bio: String,
    dob: String,
    gender: String,
    phone: String,
    education_level: String,
    place_of_birth: String,
    relationship_status: String,
    dependants: Number,
    employment_status: String,
    job_type: String,
    occupation: String,
    address: String,
    city: String,
    county: String,
    postal_code: String,
    disability: Boolean,
    disability_specify: String,
    next_of_the_kin_full_name: String,
    next_of_the_kin_phone: String,
    gp_name: String,
    gp_phone: String,
  },
  user_settings: {
    email_notifications: Boolean,
    push_notifications: Boolean,
    inactive_pushes_sent: Number,
  },
};
const defaultClient = {
  client_logo: "/img/spectrum_life_logo._light.png",
  client_name: "Default Client",
  currency: "eur",
  eap_active: false,
  logo: "/img/spectrum_life_logo._light.png",
  skin: 0,
  success: true,
  two_factor_enabled: false,
  google_sso_enabled: 0,
  ms_sso_enabled: 0,
  login_page_image: null,
  base_hex: null,
};
export default new Vuex.Store({
  state: {
    user: defaultUser,
    client: defaultClient,
    appointments: false,
    warning: null,
    cutOffCounter: 10,
    event: {},
    isIE: true,
    isSaml: false,
    home_page: "old_default",
    nav: [],
    sideNav: [],
    navElements: [],
    homeRoute: "/",
    subdomain: "app",
    accessCode: 0,
    lang: "en",
    pageTitle: "",
    previousRoute: null,
    podcastQueue: [],
    podcastQueuePosition: 0,
    darkMode: false,
    // Defaults to `true` (nothing pending). A fresh tab rehydrates the logged-in
    // user from localStorage but never re-runs `setUser`, and this flag is not in
    // the vuex-persistedstate whitelist, so it stays `true` there. Only an actual
    // login (`setUser`) flips it to `false`, scoping the verify-email prompt to
    // once per login rather than every tab or reload.
    emailVerificationPromptResolved: true,
    navType: "standard",
    // The member's effective timezone (TD-192). "" until get-timezone resolves;
    // consumers fall back to the device clock only while it is empty.
    userTimezone: "",
    bookingReminderLastSeen: "",
    show_theme_select_in_nav: false,
    companyBoard: false,
    default_nav_elements: {
      choose_language: "Choose Language",
      favourites: "Favourites",
      home: "Home",
      info: "Info",
      logout: "Logout",
      my_progress: "My Progress",
      my_list: "My List",
      profile: "Profile",
      what_are_you_looking_for: "What are you looking for...",
      dark_mode: "Dark Mode",
    },
    admin_nav_elements: {
      data_analytics: "Data Analytics",
      branding_customise: "Branding Customise",
      custom_content: "Custom Content",
      user_management: "User Management",
      exit_admin_view: "Exit Admin View",
      enter_admin_view: "Enter Admin View",
    },
    distanceUnit: "Kilometers",
    temporary_token: null,
    twoFactorChallenge: readStoredTwoFactorChallenge(),
    serviceData: null,
    pulseLanding: null,
    pulseTrends: null,
    pulseReport: null,
    // null until the pulse_insights_v2_enabled feature flag is fetched once per
    // session; true/false thereafter (see ensurePulseInsightsV2Flag)
    pulseInsightsV2Enabled: null,
    embedAuthPending: false,
    embedAuthError: null,
  },
  getters: {
    //TODO:: use mapState appropriately instead of getters
    user: (state) => state.user,
    user_id: (state) => state.user.user_id,
    appointments: (state) => state.appointments,
    country: (state) => state.user.user.country,
    client: (state) => state.client,
    coach: (state) => state.user.coach,
    warning: (state) => state.warning,
    skin: (state) => state.client.skin,
    event: (state) => state.event,
    currency: (state) => state.client.currency,
    isIE: (state) => state.isIE,
    isSaml: (state) => state.isSaml,
    nav: (state) => state.nav,
    sideNav: (state) => state.sideNav,
    navElements: (state) => state.navElements,
    homeRoute: (state) => state.homeRoute,
    subdomain: (state) => state.subdomain,
    hardcoded: (state) => {
      if (hardcodedBranding[state.subdomain]) {
        return hardcodedBranding[state.subdomain];
      } else return false;
    },
    podcastQueue: (state) => state.podcastQueue,
    podcastQueuePosition: (state) => state.podcastQueuePosition,
    darkMode: (state) => state.darkMode,
    navType: (state) => state.navType,
    userTimezone: (state) => state.userTimezone,
    bookingReminderLastSeen: (state) => state.bookingReminderLastSeen,
    lang: (state) => state.lang,
    default_nav_elements: (state) => state.default_nav_elements,
    courseProgress: (state) => state.user.user.course_progress,
    pulseLanding: state => state.pulseLanding,
    pulseTrends: state => state.pulseTrends,
    pulseReport: state => state.pulseReport,
    pulseInsightsV2Enabled: state => state.pulseInsightsV2Enabled,
    myList: (state) => state.user.user.my_list,
    emailVerified: (state) => state.user.user.email_verified,
    emailVerificationPromptResolved: (state) =>
      state.emailVerificationPromptResolved,
    admin_nav_elements: (state) => state.admin_nav_elements,
    companyBoard: (state) => state.companyBoard,
    distanceUnit: (state) => state.distanceUnit,
    temporaryToken: (state) => state.temporary_token,
    twoFactorChallenge: (state) =>
      state.twoFactorChallenge || EMPTY_TWO_FACTOR_CHALLENGE,
    embedAuthPending: state => state.embedAuthPending,
    embedAuthError: state => state.embedAuthError,
    showThemeSelectInNav: (state) => state.show_theme_select_in_nav,
  },
  mutations: {
    // Zurich embed: the scoped access token is the session credential (renewal
    // spec §1), not the account's long-lived oath_token that
    // authenticate-with-token hands back. Every API call carries it as
    // user_id, and a renewal swaps it in place without touching the rest of
    // the session.
    setEmbedToken(state, token) {
      state.user.user_token = token;
      state.user.user_id = token;
    },
    setUser(state, user) {
      // A real login re-arms the verify-email prompt. `setUser` runs only in the
      // tab where the login happens; other tabs rehydrate from localStorage
      // without calling it, so this un-resolves the prompt for the login tab
      // alone — showing the banner once post-login instead of on every tab.
      state.emailVerificationPromptResolved = false;
      state.user.user_token = user.user_token;
      state.user.user_role = user.user_role;
      state.user.username = user.username;
      state.user.v3_ready = user.v3_ready;
      state.user.user_id = user.user_id;
      state.user.user_client_id = user.user_client_id;
      state.user.up_domain = user.up_domain;
      state.user.authenticated = user.authenticated;
      state.user.user = user.user;
      state.user.user_pp_slug = user.pp_slug;
      state.user.user_tc_slug = user.tc_slug;
      state.user.coach = user.coach;
      state.user.home_page = user.home_page;
      state.user.access_code_user = user.access_code_user;
      state.companyBoard = user.company_board;
      state.course_progress = user.course_progress;
      state.user.my_list = user.my_list;
      if (user.authenticated && user.user_id && user.user_client_id) {
        identifyUser({
          userId: String(user.user.user_id),
          clientId: String(user.user_client_id),
          userRole: user.user_role,
          lang: "",
        });
        identifyIntercomUser({
          userId: String(user.user.user_id),
          clientId: String(user.user_client_id),
          userRole: user.user_role,
          skin: state.client.skin,
          userHash: user.user.intercom_user_hash,
        });
      }
    },
    setUserTimezone(state, timezone) {
      state.userTimezone = timezone;
    },
    setEmailVerified(state, value) {
      state.user.user.email_verified = value;
    },
    setEmailVerificationPromptResolved(state, value) {
      state.emailVerificationPromptResolved = value;
    },
    setTherapyType(state, therapy) {
      state.user.therapy.area = therapy.area;
      state.user.therapy.age_group = therapy.age_group;
      state.user.therapy.membership_id = therapy.membership_id;
    },
    clearTherapyDetails(state, therapy) {
      state.user.therapy.area = null;
      state.user.therapy.age_group = null;
      state.user.therapy.membership_id = null;
    },
    setUserProfile(state, user_profile) {
      state.user.user_profile.image_url = user_profile.image_url;
      state.user.user_profile.bio = user_profile.bio;
      state.user.user_profile.gender = user_profile.gender;
      state.user.user_profile.phone = user_profile.phone;
      state.user.user_profile.dob = user_profile.dob;
      state.user.user_profile.education_level = user_profile.education_level;
      state.user.user_profile.place_of_birth = user_profile.place_of_birth;
      state.user.user_profile.relationship_status =
        user_profile.relationship_status;
      state.user.user_profile.dependants = user_profile.dependants;
      state.user.user_profile.employment_status =
        user_profile.employment_status;
      state.user.user_profile.job_type = user_profile.job_type;
      state.user.user_profile.occupation = user_profile.occupation;
      state.user.user_profile.relationship_status =
        user_profile.relationship_status;
      state.user.user_profile.address = user_profile.address;
      state.user.user_profile.city = user_profile.city;
      state.user.user_profile.county = user_profile.county;
      state.user.user_profile.postal_code = user_profile.postal_code;
      state.user.user_profile.disability = user_profile.disability;
      state.user.user_profile.disability_specify =
        user_profile.disability_specify;
      state.user.user_profile.next_of_the_kin_full_name =
        user_profile.next_of_the_kin_full_name;
      state.user.user_profile.next_of_the_kin_phone =
        user_profile.next_of_the_kin_phone;
      state.user.user_profile.gp_name = user_profile.gp_name;
      state.user.user_profile.gp_phone = user_profile.gp_phone;
    },
    setUserSettings(state, user_settings) {
      state.user.user_settings.email_notifications =
        user_settings.email_notifications;
      state.user.user_settings.push_notifications =
        user_settings.push_notifications;
      state.user.user_settings.inactive_pushes_sent =
        user_settings.inactive_pushes_sent;
    },
    setUserInsightsStatus(state, insights_completed) {
      state.user.insights_completed = insights_completed;
    },
    setPulseLanding(state, pulseLanding) {
      state.pulseLanding = pulseLanding;
    },
    // patch reminders_enabled in place so the pulseLanding reference stays
    // stable — replacing the whole object would cascade into every landing-
    // derived computed (e.g. the trend chart's track list) and force the
    // chart to re-render on a reminders toggle. Plain assignment (the field
    // already exists from get-landing) touches only its own dependency.
    setPulseRemindersEnabled(state, enabled) {
      if (state.pulseLanding) {
        state.pulseLanding.reminders_enabled = enabled;
      }
    },
    setPulseTrends(state, pulseTrends) {
      state.pulseTrends = pulseTrends;
    },
    setPulseReport(state, pulseReport) {
      state.pulseReport = pulseReport;
    },
    setPulseInsightsV2Enabled(state, enabled) {
      state.pulseInsightsV2Enabled = enabled;
    },
    setClientBranding(state, client) {
      state.client.client_name = client.client_name;
      state.client.client_logo = client.client_logo;
      state.client.eap_active = client.eap_active;
      state.client.currency = client.currency;
      state.client.skin = client.skin;
      state.client.logo = client.logo;
      state.accessCode = client.access_code;
      state.client.pp_slug = client.pp_slug;
      state.client.tc_slug = client.tc_slug;
      state.client.two_factor_enabled = client.two_factor_enabled;
      state.client.google_sso_enabled = client.google_sso_enabled;
      state.client.ms_sso_enabled = client.ms_sso_enabled;
      state.client.login_page_image = client.login_page_image;
      state.client.base_hex = client.base_hex || null;
      if (!state.user.authenticated) {
        state.user.home_page = client.home_page;
      }
    },
    setSkin(state, skin) {
      state.client.skin = skin;
    },
    setTwoFactorEnabled(state, twoFactorEnabled) {
      state.client.two_factor_enabled = twoFactorEnabled;
    },
    setBrowserIE(state, isIE) {
      state.isIE = isIE;
    },
    setSamlTrue(state) {
      state.isSaml = true;
    },
    setWarning(state, warning) {
      state.warning = warning;
    },
    acceptWarning(state) {
      state.warning = null;
    },
    logout(state) {
      state.user = defaultUser;
      state.isSaml = false;
      state.emailVerificationPromptResolved = true;
      // Cleared with the session — it is this member's preference, not the device's.
      state.userTimezone = "";
      //state.client = defaultClient;
    },
    viewEvent(state, event) {
      state.event = event;
    },
    setNav(state, nav) {
      state.nav = nav;
    },
    setSideNav(state, sideNav) {
      state.sideNav = sideNav;
    },
    setNavElements(state, navElements) {
      state.navElements = navElements;
    },
    setHomeRoute(state, home) {
      state.homeRoute = home;
    },
    setDomain(state, domain) {
      state.subdomain = domain;
    },
    setAccessCode(state, hasCode) {
      state.accessCode = hasCode;
    },
    setLanguage(state, language) {
      const availableLanguages = [
        "en",
        "de",
        "fr",
        "it",
        "es",
        "pt",
        "zh",
        "ru",
      ];
      if (availableLanguages.includes(language)) {
        state.lang = language;
        registerLang(language);
      }
    },
    setPageTitle(state, title) {
      state.pageTitle = title;
    },
    setPreviousRoute(state, route) {
      state.previousRoute = route;
    },
    setPodcastQueue(state, podcastQueue) {
      state.podcastQueue = podcastQueue;
    },
    setPodcastQueuePosition(state, podcastQueuePosition) {
      state.podcastQueuePosition = podcastQueuePosition;
    },
    setUserTermsAgreementTrue(state) {
      state.user.user.tc_accepted = true;
    },
    setUserPPAgreementTrue(state) {
      state.user.user.pp_accepted = true;
    },
    setCoach(state, coach) {
      state.user.coach = coach;
    },
    setUserCookiesAgreementTrue(state) {
      state.user.user.cookies_accepted = true;
    },
    setDarkMode(state, darkMode) {
      state.darkMode = darkMode;
    },
    setNavType(state, navType) {
      const layoutsThatUseVersionTwoNav = ["default"];
      let homePage = navType;
      if (state.user.authenticated) {
        homePage = state.user.home_page;
      }
      if (layoutsThatUseVersionTwoNav.includes(homePage)) {
        state.navType = "versionTwo";
      } else {
        state.navType = "standard";
      }
    },
    setBookingReminderLastSeen(state) {
      state.bookingReminderLastSeen = moment().format("YYYY-MM-DD");
    },
    setEmbedAuthPending(state, pending) {
      state.embedAuthPending = pending;
    },
    setEmbedAuthError(state, reason) {
      state.embedAuthError = reason;
    },
    setInitialCourseProgress(state) {
      if (state.user.user.hasOwnProperty("course_progress")) return;
      state.user.user.course_progress = [];
    },
    setCourseProgress(state, course) {
      let exists = false;
      if (!course.id || course.percent === 0) return;
      if (course.percent === 1) {
        state.user.user.course_progress = state.user.user.course_progress.filter(
          (item) => {
            return (
              item.channel !== course.channel &&
              Number.parseInt(item.id) !== Number.parseInt(course.id)
            );
          },
        );
      } else {
        if (!state.user.user.course_progress.length) {
          state.user.user.course_progress = [course];
        }
        state.user.user.course_progress.map((item, i) => {
          if (
            item.channel === course.channel &&
            Number.parseInt(item.id) === Number.parseInt(course.id)
          ) {
            exists = true;
            state.user.user.course_progress[i] = course;
          }
        });
        if (!exists) state.user.user.course_progress.push(course);
      }
    },
    clearCourseProgress(state) {
      state.user.user.course_progress = [];
    },
    setMyList(state, list) {
      state.user.user.my_list = list;
    },
    toggleDistanceUnit(state) {
      if (state.distanceUnit === "Kilometers") {
        state.distanceUnit = "Miles";
      } else {
        state.distanceUnit = "Kilometers";
      }
    },
    setTemporaryToken(state, token) {
      state.temporary_token = token;
    },
    clearTemporaryToken(state) {
      state.temporary_token = null;
    },
    setTwoFactorChallenge(state, challenge) {
      state.twoFactorChallenge = {
        ...EMPTY_TWO_FACTOR_CHALLENGE,
        ...challenge,
      };
      persistTwoFactorChallenge(state.twoFactorChallenge);
    },
    clearTwoFactorChallenge(state) {
      state.twoFactorChallenge = { ...EMPTY_TWO_FACTOR_CHALLENGE };
      persistTwoFactorChallenge(state.twoFactorChallenge);
    },
    toggleShowThemeSelectInNav(state) {
      state.show_theme_select_in_nav = !state.show_theme_select_in_nav;
    },
    setServiceData(state, service) {
      state.serviceData = service;
    },
    setAppointments(state, appointments) {
      state.appointments = appointments;
    },
  },
  actions: {
    // Fetch the pulse_insights_v2_enabled feature flag once per session and
    // cache it. Resolves to the boolean. On failure it returns false (legacy)
    // without caching, so a transient error doesn't pin the user to legacy for
    // the whole session — the next navigation retries.
    async ensurePulseInsightsV2Flag({ state, commit }) {
      if (state.pulseInsightsV2Enabled !== null) {
        return state.pulseInsightsV2Enabled;
      }
      try {
        const res = await axios.post(
          config.api_env + "/application/api/u/feature-flag",
          {
            user_id: state.user.user_id,
            feature_flags: ["pulse_insights_v2_enabled"],
          },
          config.options
        );
        const enabled = !!(
          res.data &&
          res.data.feature_flags &&
          res.data.feature_flags.pulse_insights_v2_enabled
        );
        commit("setPulseInsightsV2Enabled", enabled);
        return enabled;
      } catch (e) {
        return false;
      }
    },
    setTherapyUser(context, payload) {
      context.commit("setUser", {
        user: payload.user,
        user_id: payload.user.user_token,
      });
      context.commit("setTherapyType", {
        age_group: payload.therapy.age_group,
        area: payload.therapy.area,
        membership_id: payload.therapy.membership_id,
      });
    },
    setTwoFactorEnabled(context, payload) {
      context.commit("setTwoFactorEnabled", payload);
    },
    setServiceData(context, service) {
      context.commit("setServiceData", service);
    },
    setBannerDisplayInfo(context, bannerDisplayInfo) {
      context.commit("setBannerDisplayInfo", bannerDisplayInfo);
    },
    setUserProfile(context, payload) {
      context.commit("setUserProfile", payload);
    },
    setTemporaryToken(context, payload) {
      context.commit("setTemporaryToken", payload);
    },
    setUserSettings(context, payload) {
      context.commit("setUserSettings", payload);
    },
    async twoFactorSignIn(context, payload) {
      return twoFactorSignInGuard.run(async () => {
        try {
          let res = await axios.post(
            config.api_env + "/application/api-hc/two-factor-sign-in",
            payload.payload,
            config.options,
          );
          if (res.data.success === true) {
            context.commit("clearTwoFactorChallenge");
            context.commit("setUser", res.data);
            trackEvent(APP_OPENED, { platform: "web" });
            context.commit("setLanguage", res.data.lang);
            //check if user is on v3 portal - if yes continue - if find their organisation
            if (res.data.v3_ready) {
              //if the subdomain is laya wellbeing don't get the clients branding
              if (context.state.subdomain !== "laya-wellbeing") {
                context.dispatch("getClientBranding", res.data.user_token);
                context.dispatch("getClientInsights", res.data.user_token);
              }
              const homeroute = await context.dispatch(
                "getNav",
                res.data.user_token,
              );
              context.state.previousRoute
                ? payload.router.push(context.state.previousRoute.fullPath)
                : homeroute
                ? payload.router.push(homeroute)
                : payload.router.push("/");
              return true;
            } else return false;
          } else {
            await context.commit("setWarning", res.data.err);
          }
        } catch (e) {
          await context.commit(
            "setWarning",
            "We could not verify your code. Please try again.",
          );
        }
      });
    },
    async twoFactorResend(context, token) {
      return twoFactorResendGuard.run(async () => {
        const result = await resendTwoFactorCode(token, axios.post);
        if (!result.success) {
          await context.commit("setWarning", result.error);
        }
        return result;
      });
    },
    setTwoFactorChallenge(context, challenge) {
      context.commit("setTwoFactorChallenge", challenge);
    },
    clearTwoFactorChallenge(context) {
      context.commit("clearTwoFactorChallenge");
    },
    setWarning(context, payload) {
      context.commit("setWarning", payload);
    },
    // Sends the SMS code for MFA users who had no mobile on file. `payload` is
    // { token: oath_token, phone: <E.164> }. Returns the temporary_token for the
    // verify screen on success, or null (with a warning surfaced) on failure.
    async twoFactorSend(context, payload) {
      const sendResult = await sendTwoFactorCode(
        payload.token,
        payload.phone,
        axios.post,
      );
      if (sendResult.success && sendResult.token) {
        return sendResult.token;
      }
      await context.commit(
        "setWarning",
        sendResult.err || TWO_FACTOR_SEND_INVALID_PHONE_MESSAGE,
      );
      return null;
    },

    async googleAuthenticate(context, payload) {
      let res = await axios.post(
        config.api_env + "/application/api/u/oauth/google/authenticate",
        payload.payload,
        config.options,
      );
      if (res.data.success) {
        if (res.data.hasOwnProperty("data")) {
          if (res.data.data.hasOwnProperty("oAuthAccessToken")) {
            context.commit("setTemporaryToken", res.data.data.oAuthAccessToken);
            return;
          }
        }
        context.commit("setUser", res.data);
        trackAppOpened();
        context.commit("setInitialCourseProgress");
        context.commit("setLanguage", res.data.lang);
        context.commit("setNavType", res.data.home_page);

        if (res.data.v3_ready) {
          if (context.state.subdomain !== "laya-wellbeing") {
            context.dispatch("getClientBranding", res.data.user_token);
            context.dispatch("getClientInsights", res.data.user_token);
          }

          const homeroute = await context.dispatch(
            "getNav",
            res.data.user_token,
          );
          context.state.previousRoute
            ? payload.router.push(context.state.previousRoute.fullPath)
            : homeroute
            ? payload.router.push(homeroute)
            : payload.router.push("/");

          return true;
        }

        return false;
      } else {
        await context.commit("setWarning", res.data.err);
      }
    },
    async oauthSignIn(context, payload) {
      try {
        context.commit("setUser", payload);
        trackAppOpened();
        context.commit("setInitialCourseProgress");
        context.commit("setLanguage", payload.lang);
        context.commit("setNavType", payload.home_page);
        //check if user is on v3 portal - if yes continue - if find their organisation
        if (payload.v3_ready) {
          //if the subdomain is laya wellbeing don't get the clients branding
          if (context.state.subdomain !== "laya-wellbeing") {
            context.dispatch("getClientBranding", payload.user_token);
            context.dispatch("getClientInsights", payload.user_token);
          }
          const homeroute = await context.dispatch(
            "getNav",
            payload.user_token,
          );
          return true;
        }
      } catch (e) {}
    },
    async signInV2(context, payload) {
      try {
        const timeoutPromise = new Promise((resolve, reject) => {
          setTimeout(() => {
            resolve("timed-out");
          }, 30000);
        });
        let api = axios.post(
          config.api_env + "/application/api-hc/authenticate",
          payload.payload,
          config.options,
        );
        const res = await Promise.race([api, timeoutPromise]);
        if (res === "timed-out") {
          EventBus.$emit("login-time-out");
          return false;
        }
        if (res.data.hasOwnProperty("two_factor")) {
          if (res.data.two_factor) {
            context.commit("clearTwoFactorChallenge");
            payload.router.push(
              "/two-factor?oauth=" +
                res.data.token +
                (res.data.phone_last4
                  ? "&phone_last4=" + res.data.phone_last4
                  : ""),
            );
            return;
          }
        }
        if (res.data.success === true) {
          context.commit("setUser", res.data);
          trackEvent(APP_OPENED, { platform: "web" });
          context.commit("setInitialCourseProgress");
          context.commit("setLanguage", res.data.lang);
          context.commit("setNavType", res.data.home_page);
          //check if user is on v3 portal - if yes continue - if find their organisation
          if (res.data.v3_ready) {
            //if the subdomain is laya wellbeing don't get the clients branding
            if (context.state.subdomain !== "laya-wellbeing") {
              context.dispatch("getClientBranding", res.data.user_token);
              context.dispatch("getClientInsights", res.data.user_token);
            }
            const homeroute = await context.dispatch(
              "getNav",
              res.data.user_token,
            );
            context.state.previousRoute
              ? payload.router.push(context.state.previousRoute.fullPath)
              : homeroute
              ? payload.router.push(homeroute)
              : payload.router.push("/");
            return true;
          } else return false;
        } else {
          EventBus.$emit("login-unsuccessful");
          await context.commit("setWarning", res.data.err);
        }
      } catch (e) {
        //console.log(e);
      }
    },
    async signIn(context, payload) {
      try {
        const timeoutPromise = new Promise((resolve, reject) => {
          setTimeout(() => {
            resolve({ timedOut: true });
          }, 15000);
        });

        const apiPromise = axios.post(
          config.api_env + "/application/api-hc/authenticate",
          payload.payload,
          config.options,
        );

        const res = await Promise.race([apiPromise, timeoutPromise]);

        // Handle the case where the timeout occurred
        if (res.timedOut) {
          EventBus.$emit("login-time-out");
          return false;
        }

        if (res.data.hasOwnProperty("two_factor")) {
          if (res.data.two_factor) {
            context.commit("clearTwoFactorChallenge");
            // phone_required means the MFA user has no mobile on file: collect one
            // (phone-capture screen) before a code is sent. The token here is an
            // oath_token used only for /two-factor-send, not the verify token.
            if (res.data.phone_required) {
              payload.router.push(
                "/two-factor?oauth=" + res.data.token + "&phone_required=1",
              );
            } else {
              payload.router.push(
                "/two-factor?oauth=" +
                  res.data.token +
                  (res.data.phone_last4
                    ? "&phone_last4=" + res.data.phone_last4
                    : ""),
              );
            }
            return;
          }
        }

        if (res.data.hasOwnProperty("boa_clients")) {
          EventBus.$emit("login-boa", res.data.boa_clients);
        } else if (res.data.success === true) {
          context.commit("setUser", res.data);
          trackEvent(APP_OPENED, { platform: "web" });
          context.commit("setInitialCourseProgress");
          context.commit("setLanguage", res.data.lang);
          context.commit("setNavType", res.data.home_page);

          const goodMorningBanner =
            res.data?.user?.banners_display_info?.good_morning_banner;
          localStorage.setItem("goodMorningBanner", goodMorningBanner);

          // Check if user is on v3 portal - if yes continue - if find their organisation
          if (res.data.v3_ready) {
            if (context.state.subdomain !== "laya-wellbeing") {
              context.dispatch("getClientBranding", res.data.user_token);
              context.dispatch("getClientInsights", res.data.user_token);
            }
            const homeroute = await context.dispatch(
              "getNav",
              res.data.user_token,
            );

            const referralString = localStorage.getItem("referral");
            const referralData = referralString
              ? JSON.parse(referralString)
              : null;
            const marketplaceServiceId = referralData?.marketplace_service_id;
            const isReferralFlow = payload.router.currentRoute.query.referral;

            isReferralFlow && marketplaceServiceId
              ? payload.router.push(`/services/${marketplaceServiceId}`)
              : context.state.previousRoute
              ? payload.router.push(context.state.previousRoute.fullPath)
              : homeroute
              ? payload.router.push(homeroute)
              : payload.router.push("/");
            return true;
          } else {
            EventBus.$emit("login-unsuccessful");
            return false;
          }
        } else {
          EventBus.$emit("login-unsuccessful");
          await context.commit("setWarning", res.data.err);
          return false;
        }
      } catch (e) {
        if (e.response.status === 408) {
          EventBus.$emit("login-time-out");
        } else {
          EventBus.$emit("login-unsuccessful");
        }
        return false;
      }
    },
    async accessCodeLogin(context, payload) {
      //try to get the nav for a user with the access code, if it returns then it's a valid code.
      const homeroute = await context.dispatch("getNav", payload.accessCode);
      if (homeroute) {
        const user = {
          authenticated: true,
          success: true,
          up_domain: "up",
          insights_completed: true,
          user_client_id: Number,
          user_id: payload.accessCode,
          user_role: "cl",
          user_token: payload.accessCode,
          username: payload.accessCode,
          v3_ready: true,
        };
        context.commit("setUser", user);
        context.commit("setInitialCourseProgress");
        context.dispatch("getClientBranding", payload.accessCode);
        payload.router.push(homeroute);
      } else {
        await context.commit(
          "setWarning",
          "Sorry that access code is not recognised",
        );
      }
    },
    async retailTrustSignIn(context, payload) {
      // Retail Trust embed: the b2b/token exchange returns an OAuth access_token that is
      // NOT a CORE-API session on its own — posting it as user_id fails "Not a valid
      // user". resolve-token turns that OAuth token into a real session, returning the
      // full AuthSuccess (session user_token + user + client) in one call — then setUser
      // + branding + nav, exactly like a normal sign-in.
      try {
        // VUE_APP_RT_RESOLVE_URL lets the local mock harness stand in for this call
        // (see rt-mock/). Unset everywhere but local dev, so deployed builds always
        // hit the real resolve-token endpoint.
        const resolveUrl =
          process.env.VUE_APP_RT_RESOLVE_URL ||
          config.api_env + "/application/api/u/user/resolve-token";
        const res = await axios.post(
          resolveUrl,
          { token: payload.accessToken, source: "web" },
          { headers: { "Content-Type": "application/json" } }
        );
        if (res.data.success !== true) return false;
        context.commit("setUser", res.data);
        trackEvent(APP_OPENED, { platform: "web" });
        context.commit("setInitialCourseProgress");
        context.commit("setLanguage", res.data.lang);
        context.commit("setNavType", res.data.home_page);
        if (context.state.subdomain !== "laya-wellbeing") {
          context.dispatch("getClientBranding", res.data.user_token);
          context.dispatch("getClientInsights", res.data.user_token);
        }
        const homeroute = await context.dispatch("getNav", res.data.user_token);
        // Preserve a deep link. A partner can point the iframe straight at a service
        // (e.g. /session-booking?service_type=dmh); the app sits on that route showing
        // the sign-in view until the handshake completes, so pushing home here would
        // throw the deep link away. Only redirect when we're on the root route.
        const currentPath = payload.router && payload.router.currentRoute
          ? payload.router.currentRoute.path
          : "/";
        if (payload.router && currentPath === "/") payload.router.push(homeroute || "/");
        return true;
      } catch (e) {
        return false;
      }
    },
    async embedSignIn(context, payload) {
      // Zurich embed: turn the scoped token from the embed handshake into a full
      // session. Mirrors newCodeLogin — the difference is the entry point (a token
      // from the postMessage handshake, not a user-typed code) and that failures
      // surface via the sl:error postMessage rather than an on-page warning.
      // The token arrives already valid: it is a Spectrum access token carrying
      // the `user` scope, so no separate exchange is needed. authenticate-with-token
      // resolves it and returns the same auth payload a code login produces, which
      // is what setUser expects. (The earlier /api/u/iframe/authenticate was
      // provisional and does not exist.)
      let res;
      try {
        res = await axios.post(
          config.api_env + "/application/api/u/user/authenticate-with-token",
          { user_id: payload.token },
          config.options
        );
      } catch (e) {
        // Logged rather than swallowed: this reaches the parent as a bare
        // "auth_failed", which says nothing about which of several very
        // different causes it was.
        console.error("[embed] authenticate-with-token could not be reached", e);
        return false;
      }

      if (res.data.success !== true) {
        console.error("[embed] authenticate-with-token refused the token", res.data);
        return false;
      }

      context.commit("setUser", res.data);
      // Swap the long-lived oath_token for the scoped token before anything
      // else uses the session: the oath_token never expires, and an embed must
      // not hold a credential that outlives its renewals.
      context.commit("setEmbedToken", payload.token);

      // Authentication is finished here. Everything below is post-login setup,
      // and it gets its own guard on purpose: a failure in analytics, nav or
      // routing used to be reported to the parent as an authentication failure,
      // which sends anyone debugging it to the token and the endpoint — neither
      // of which is at fault. The user is signed in either way.
      try {
        trackEvent(APP_OPENED, { platform: "web" });
        context.commit("setInitialCourseProgress");
        context.commit("setLanguage", res.data.lang);
        if (context.state.subdomain !== "laya-wellbeing") {
          context.dispatch("getClientBranding", payload.token);
          context.dispatch("getClientInsights", payload.token);
        }
      } catch (e) {
        console.error("[embed] signed in, but post-login setup failed", e);
      }

      // Navigation sits outside that block on purpose. It used to be the last
      // statement inside it, downstream of getNav — so anything throwing before
      // it, getNav most of all, skipped the redirect entirely. The app then sat
      // on the login screen it had rendered before parent:init arrived, while
      // sign-in reported success and nothing was logged. Embedded, that reads as
      // "the iframe just shows a login page".
      let target = "/";
      try {
        target = (await context.dispatch("getNav", payload.token)) || "/";
      } catch (e) {
        console.error("[embed] getNav failed; falling back to /", e);
      }

      // Preserve a deep link. The partner points the iframe straight at a
      // service (/services/sl_dmh); logged out, LoggedIn records that route as
      // previousRoute and sends the app to /login, which is where parent:init
      // finds it. So from the root or the login screen, go back to the deep link
      // when there is one — the same thing every other sign-in here does — and
      // home otherwise. Anywhere else the member is already where they belong.
      const currentPath = payload.router.currentRoute.path;
      if (currentPath === "/" || currentPath === "/login") {
        const previous = context.state.previousRoute;
        if (previous && previous.fullPath && previous.fullPath !== "/login") {
          target = previous.fullPath;
        }
      }
      if ((currentPath === "/" || currentPath === "/login") && currentPath !== target) {
        // vue-router 3 rejects a push to the route already showing
        // (NavigationDuplicated) and when a guard redirects elsewhere. Neither
        // is a sign-in failure, but neither should pass unnoticed either.
        await payload.router.push(target).catch((e) => {
          console.warn("[embed] navigation to " + target + " did not complete", e && e.message);
        });
      }

      return true;
    },
    async newCodeLogin(context, payload) {
      console.log(payload);
      try {
        let res = await axios.post(
          config.api_env + "/application/api/u/user/access-code-signup",
          payload.payload,
          config.options,
        );
        if (res.data.success === true) {
          context.commit("setUser", res.data);
          trackEvent(APP_OPENED, { platform: "web" });
          context.commit("setInitialCourseProgress");
          context.commit("setLanguage", res.data.lang);
          //check if user is on v3 portal - if yes continue - if find their organisation
          if (res.data.v3_ready) {
            //if the subdomain is laya wellbeing don't get the clients branding
            if (context.state.subdomain !== "laya-wellbeing") {
              context.dispatch("getClientBranding", res.data.user_token);
              context.dispatch("getClientInsights", res.data.user_token);
            }
            const homeroute = await context.dispatch(
              "getNav",
              res.data.user_token,
            );
            context.state.previousRoute
              ? payload.router.push(context.state.previousRoute.fullPath)
              : homeroute
              ? payload.router.push(homeroute)
              : payload.router.push("/");
            return true;
          } else return false;
        } else {
          await context.commit("setWarning", res.data.msg);
        }
      } catch (e) {
        //console.log(e);
      }
    },
    async getNav(context, token) {
      const payload = {
        user_id: token,
        platform: "web",
        language: context.getters.lang,
      };
      let res = await axios.post(
        config.api_env + "/application/api/u/settings/get-nav",
        payload,
        config.options,
      );
      if (res.data.success) {
        if (res.data.side_nav) {
          context.commit("setSideNav", res.data.side_nav);
        }
        if (res.data.nav_elements) {
          context.commit("setNavElements", res.data.nav_elements);
        }
        if (res.data.nav) {
          context.commit("setNav", res.data.nav);
          return "/";
        }
      } else return 0;
    },
    async requestNepHandoffToken(context) {
      const payload = { user_id: context.getters.user.user_token };
      const res = await axios.post(
        config.api_env + "/application/api/u/nep-handoff/mint",
        payload,
        config.options,
      );
      if (res.data.success) {
        return res.data.handoff_token;
      }
      return null;
    },
    async loginAs(context, user) {
      context.commit("setUser", user.user_info);
      // Covers signup and admin impersonation — both go through loginAs.
      trackEvent(APP_OPENED, { platform: "web" });
      await context.dispatch("getClientBranding", user.user_token);
      await context.dispatch("getClientInsights", user.user_token);
      const nav = await context.dispatch("getNav", user.user_token);
      context.commit("setInitialCourseProgress");
      if (nav) {
        return nav;
      } else {
        context.commit("setWarning", "User not valid");
        return "/login";
      }
      //return "/";
    },
    async loadUserTimezone(context) {
      if (!isTimezoneSupportEnabled()) return;

      const res = await fetchUserTimezone(context.getters.user_id);
      // `timezone` is the effective zone and is always valid when success is
      // true; an explicit choice and an inherited regional default are both
      // correct to display against.
      if (res && res.success && res.timezone) {
        context.commit("setUserTimezone", res.timezone);
      }
    },
    async checkEmailVerified(context) {
      try {
        const res = await axios.post(
          config.api_env + "/application/api/u/user/check-email-verified",
          { user_id: context.getters.user_id },
          config.options,
        );
        if (res.data.success) {
          context.commit("setEmailVerified", res.data.email_verified);
        }
      } catch (e) {
        // fail open — don't block the user on a network error
      }
    },
    async sendEmailVerification(context) {
      const res = await axios.post(
        config.api_env + "/application/api/u/user/send-email-verification",
        { user_id: context.getters.user_id },
        config.options,
      );
      return res.data.success;
    },
    async getClientBranding(context, token) {
      let payload = {
        user_id: token,
      };
      try {
        let clientOptions = await axios.post(
          config.api_env +
            "/application/api/customisations/get-client-branding",
          payload,
          config.options,
        );
        if (clientOptions.data.success) {
          let client = clientOptions.data;
          if (context.getters.hardcoded && context.getters.hardcoded.skin) {
            client.skin = context.getters.hardcoded.skin;
          }
          context.commit("setClientBranding", client);
          context.commit("setNavType", clientOptions.data.home_page);
        }
      } catch (e) {
        //console.log(e);
      }
    },
    async getClientInsights(context, token) {
      let payload = {
        user_id: token,
      };
      try {
        let clientInsights = await axios.post(
          config.api_env + "/application/api-hc/completed-insights",
          payload,
          config.options,
        );
        context.commit("setUserInsightsStatus", clientInsights.data.completed);
      } catch (e) {
        //console.log(e);
      }
    },
    acceptWarning(context) {
      context.commit("acceptWarning");
    },
    async logout(context, previousRoute = null) {
      let payload = {
        user_id: context.state.user.user_token,
      };
      // Ends the embedded webchat session and clears the Twilio SDK's persisted
      // conversation state, so it isn't resumable by the next user of this machine.
      // Deliberately BEFORE the request: callers don't await this action and navigate
      // away immediately (see SideNav.logOut), so anything sequenced after the await
      // only runs if the round-trip wins that race. It was observed both firing and not
      // firing on identical logouts. Local cleanup must not depend on the network.
      signOutWebchat();
      try {
        await axios.post(
          config.api_env + "/application/api/u/user/logout",
          payload,
          config.options,
        );
      } catch (e) {
        // Same reason as signOutWebchat above: a failed round-trip must not strand the
        // session in this browser, nor drop the deep link the member arrived on.
      }
      resetIntercomUser();
      resetUser();
      if (previousRoute) {
        context.commit("setPreviousRoute", previousRoute);
      }
      context.commit("clearTherapyDetails");
      // Persisted per origin, not per session: left behind, the next user of this
      // browser inherits these bookings and Session.vue trusts a non-empty list
      // instead of fetching their own.
      context.commit("setAppointments", false);
      context.commit("setUser", defaultUser);
    },
    viewEvent(context, event) {
      context.commit("viewEvent", event);
    },
    setPodcastQueue(context, podcastQueue) {
      context.commit("setPodcastQueue", podcastQueue.queue);
      context.commit("setPodcastQueuePosition", podcastQueue.position);
    },
    setUserTermsAgreementTrue(context) {
      context.commit("setUserTermsAgreementTrue");
    },
    setUserPPAgreementTrue(context) {
      context.commit("setUserPPAgreementTrue");
    },
    setCoach(context, coach) {
      context.commit("setCoach", coach);
    },
    setUserCookiesAgreementTrue(context) {
      context.commit("setUserCookiesAgreementTrue");
    },
    setDarkMode(context, value) {
      context.commit("setDarkMode", value);
    },
    setNavType(context, value) {
      context.commit("setNavType", value);
    },
    setBookingReminderLastSeen(context) {
      context.commit("setBookingReminderLastSeen");
    },
    setSamlTrue(context) {
      context.commit("setSamlTrue");
    },
    toggleDistanceUnit(context) {
      context.commit("toggleDistanceUnit");
    },
    async selectLanguage(context, language) {
      context.commit("setLanguage", language);
      if (context.state.user.authenticated) {
        const payload = {
          user_id: context.getters.user_id,
          lang: language,
        };
        await axios.post(
          config.api_env + "/application/api/u/settings/set-user-language",
          payload,
          config.options,
        );
        let nav = await context.dispatch("getNav", context.getters.user_id);
        //location.reload();
      }
    },
    clearNav(context) {
      context.commit("setNav", []);
    },
    clearTemporaryToken(context) {
      context.commit("clearTemporaryToken");
    },
    setInitialCourseProgress(context) {
      context.commit("setInitialCourseProgress");
    },
    setCourseProgress(context, course) {
      context.commit("setCourseProgress", course);
    },
    clearCourseProgress(context) {
      context.commit("clearCourseProgress");
    },
    async setMyList(context) {
      let payload = {
        user_id: context.state.user.user_token,
      };
      try {
        let res = await axios.post(
          config.api_env + "/application/api/u/my-list",
          payload,
          config.options,
        );
        context.commit("setMyList", res.data.data);
        return res.data.success;
      } catch (e) {
        return false;
      }
    },
    async addMyListItem(context, data) {
      let payload = { ...data, user_id: context.state.user.user_token };
      try {
        let res = await axios.post(
          config.api_env + "/application/api/u/my-list/create",
          payload,
          config.options,
        );
        if (res.data.success) context.commit("setMyList", res.data.data);
        return res.data.success;
      } catch (e) {
        return false;
      }
    },
    async deleteMyListItem(context, data) {
      let payload = { ...data, user_id: context.state.user.user_token };
      try {
        let res = await axios.post(
          config.api_env + "/application/api/u/my-list/delete",
          payload,
          config.options,
        );
        if (res.data.success) context.commit("setMyList", res.data.data);
        return res.data.success;
      } catch (e) {
        return false;
      }
    },
    toggleShowThemeSelectInNav(context) {
      context.commit("toggleShowThemeSelectInNav");
    },
    setAppointments(context, appointments) {
      context.commit("setAppointments", appointments);
    },
  },
  plugins: [
    createPersistedState({
      paths: [
        "user",
        "client",
        "isIE",
        "isSaml",
        "home_page",
        "cutOffCounter",
        "nav",
        "sideNav",
        "navElements",
        "homeRoute",
        "subdomain",
        "eventsOnly",
        "accessCode",
        "darkMode",
        "navType",
        "bookingReminderLastSeen",
        "lang",
        "pageTitle",
        "companyBoard",
        "distanceUnit",
        "show_theme_select_in_nav",
        "appointments",
        // Persisted so the first paint after a reload already uses the member's
        // zone instead of visibly correcting itself once get-timezone returns.
        "userTimezone",
      ],
    }),
    displayTimeZonePlugin,
    dynamicThemePlugin,
  ],
  modules: {},
});
